import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, FileText, Newspaper, BookOpen, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import PhotoUploader from "@/components/PhotoUploader";

const CATEGORIES = [
  { key: "blog", label: "Blog", icon: BookOpen },
  { key: "news", label: "News", icon: Newspaper },
  { key: "page", label: "Static pages", icon: FileText },
];

export default function AdminContent() {
  const [category, setCategory] = useState("blog");
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [aiDrafting, setAiDrafting] = useState(false);

  function blank() {
    return { title: "", slug: "", category: "blog", excerpt: "", body_html: "", cover_photo: "", tags: "", published: false };
  }

  const runAiDraft = async () => {
    if (!form.title.trim()) { toast.error("Enter a title first"); return; }
    setAiDrafting(true);
    try {
      const { data } = await api.post("/content/ai-draft", {
        title: form.title, category: form.category || "blog", tone: "informative",
      });
      setForm(f => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        body_html: data.body_html || f.body_html,
      }));
      toast.success("AI draft ready — review & publish");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally {
      setAiDrafting(false);
    }
  };

  const load = useCallback(async () => {
    const r = await api.get("/content/admin/posts", { params: { category } });
    setPosts(r.data);
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...blank(), category });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, slug: p.slug, category: p.category, excerpt: p.excerpt || "",
      body_html: p.body_html || "", cover_photo: p.cover_photo || "",
      tags: (p.tags || []).join(", "), published: !!p.published,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.title.trim() || !form.body_html.trim()) {
      toast.error("Title and body are required");
      return;
    }
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/content/admin/posts/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/content/admin/posts", payload);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const togglePub = async (p) => {
    await api.post(`/content/admin/posts/${p.id}/toggle-publish`);
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/content/admin/posts/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-secondary">Admin · Content</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight flex items-center gap-3">
            <FileText className="w-7 h-7 text-primary" /> Blog · News · Pages
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Publish public content — visible on the marketing site.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => (window.location.href = "/app/admin/seo")} data-testid="content-seo-engine-btn">
            <Sparkles className="w-4 h-4 mr-2 text-secondary" /> SEO Engine
          </Button>
          <Button onClick={openNew} data-testid="content-new-btn">
            <Plus className="w-4 h-4 mr-2" /> New {CATEGORIES.find(c => c.key === category)?.label}
          </Button>
        </div>
      </div>

      <div className="border-b border-border">
        <nav className="flex gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`content-tab-${c.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                category === c.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <c.icon className="w-4 h-4" /> {c.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p) => (
          <div key={p.id} data-testid={`content-card-${p.id}`} className="rounded-xl border border-border bg-card p-5 space-y-2">
            <div className="flex items-start justify-between">
              <div className="font-medium">{p.title}</div>
              <Badge variant={p.published ? "default" : "secondary"}>{p.published ? "Live" : "Draft"}</Badge>
            </div>
            {p.excerpt && <div className="text-xs text-muted-foreground line-clamp-2">{p.excerpt}</div>}
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">/{p.category}/{p.slug}</div>
            <div className="flex gap-1 pt-1">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => togglePub(p)}>
                {p.published ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                {p.published ? "Unpublish" : "Publish"}
              </Button>
              <Button size="icon" variant="ghost" onClick={() => del(p)}>
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div className="col-span-3 text-center text-sm text-muted-foreground py-12">
            No {category} posts yet — create your first one.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} post</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="content-form-title" />
              </div>
              <div><Label className="text-xs">Slug (URL)</Label>
                <Input placeholder="auto from title" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Excerpt (short summary)</Label>
                <Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              </div>
              <div className="col-span-2"><Label className="text-xs">Cover photo</Label>
                <PhotoUploader value={form.cover_photo} onChange={(v) => setForm({ ...form, cover_photo: v })} purpose="other" testIdPrefix="content-cover" />
              </div>
              <div className="col-span-2"><Label className="text-xs">Body (HTML) *</Label>
                <div className="flex items-center justify-between mt-0.5">
                  <div className="text-[10px] text-muted-foreground">HTML supported. Use the AI draft button to auto-fill from your title.</div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={runAiDraft}
                    disabled={aiDrafting}
                    data-testid="ai-draft-btn"
                    className="border-secondary/50 text-secondary hover:bg-secondary/10"
                  >
                    {aiDrafting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    AI draft
                  </Button>
                </div>
                <Textarea rows={12} className="font-mono text-xs mt-2" value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} data-testid="content-form-body" placeholder="<p>Your content — HTML allowed.</p>" />
              </div>
              <div><Label className="text-xs">Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="claims, tips" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="content-form-published" />
                  Publish immediately
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="content-form-save">{editing ? "Save changes" : "Create post"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
